/**
 * Day 84.6 P1.6 — Envelope-extraction helpers.
 *
 * Pure-function narrowing for supabase-js RPC results returning typed-error
 * envelope shape. Single helper handles 13 of 16 form-facing mutation RPCs
 * (per Decision 45 envelope contract); bulk helper handles the 3 bulk RPCs
 * returning per-row TABLE shape (bulk_resolve_discrepancies +
 * bulk_transition_fulfillment_orders + bulk_update_tasks).
 *
 * Surfaced by Phase 2 weighin #P2-γ material calibration: post-migration
 * (commit 828bf33), 16 form-facing RPCs return `{ ok, data, userErrors }`
 * envelope but supabase-js Json widening absorbs the shape at compile time.
 * Consumers reading only `error` (not `data.ok`) would silently treat
 * envelope-class validation failures as success. extractEnvelope provides
 * systematic narrowing + envelope-shape validation at the consumer boundary.
 *
 * No side effects, no I/O, no logging. Pure-function discipline. Sentry
 * boundary at caller-discipline level per Q5 + #Q5-α — consumer decides
 * whether to log/capture/render userErrors. translatePostgrestError integrated
 * for the PostgREST/network error path on the single helper; bulk helper
 * doesn't auto-translate to preserve consumer flexibility on bulk-level vs
 * per-row error display.
 */

import type { PostgrestError } from '@supabase/supabase-js';
import type { BulkRpcRow, RpcEnvelope, UserError } from './errorCodes';
import { translatePostgrestError } from './translatePostgrestError';

interface SupabaseRpcResult {
  data: unknown;
  error: PostgrestError | null;
}

export interface ExtractedEnvelope<T> {
  data: T | null;
  userErrors: UserError[];
  isOk: boolean;
}

export interface ExtractedBulkEnvelope<T, TIdField extends string = 'id', TId = string> {
  rows: BulkRpcRow<T, TIdField, TId>[];
  hasAnyFailure: boolean;
  isOk: boolean;
}

export function extractEnvelope<T>(result: SupabaseRpcResult): ExtractedEnvelope<T> {
  // Path 1 — PostgREST / network error (supabase-js error present)
  if (result.error) {
    return {
      data: null,
      userErrors: [translatePostgrestError(result.error)],
      isOk: false,
    };
  }

  // Path 2 — null data, no error (edge case, treat as failure)
  if (result.data === null || result.data === undefined) {
    return {
      data: null,
      userErrors: [{ code: 'INPUT.INVALID', field_path: null, message: 'No data returned' }],
      isOk: false,
    };
  }

  // Path 3 — envelope-shape validation
  const envelope = result.data as Partial<RpcEnvelope<T>>;
  if (
    typeof envelope !== 'object' ||
    typeof envelope.ok !== 'boolean' ||
    !Array.isArray(envelope.userErrors)
  ) {
    return {
      data: null,
      userErrors: [{ code: 'INPUT.INVALID', field_path: null, message: 'Invalid envelope shape' }],
      isOk: false,
    };
  }

  // Path 4 — envelope-class failure
  if (!envelope.ok) {
    return {
      data: null,
      userErrors: envelope.userErrors,
      isOk: false,
    };
  }

  // Path 5 — success
  return {
    data: (envelope.data ?? null) as T | null,
    userErrors: [],
    isOk: true,
  };
}

export function extractBulkEnvelope<T, TIdField extends string = 'id', TId = string>(
  result: SupabaseRpcResult
): ExtractedBulkEnvelope<T, TIdField, TId> {
  // Path 1 — PostgREST / network error
  if (result.error) {
    return {
      rows: [],
      hasAnyFailure: true,
      isOk: false,
    };
  }

  // Path 2 — null or non-array data
  if (!Array.isArray(result.data)) {
    return {
      rows: [],
      hasAnyFailure: true,
      isOk: false,
    };
  }

  // Path 3 — per-row scan
  const rows = result.data as BulkRpcRow<T, TIdField, TId>[];
  const hasAnyFailure = rows.some((row) => !row.ok);

  return {
    rows,
    hasAnyFailure,
    isOk: !hasAnyFailure && result.error === null,
  };
}
